import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, Upload, CheckCircle2, Clock } from "lucide-react";

// Public page reached via /upload/:token — customer-facing, no auth.
export default function PublicUploadLink() {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [done, setDone] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/public/upload-link/${token}/verify`, { password });
      setSession(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const upload = async (docType, file) => {
    if (!file) return;
    const allowed = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes(file.type)) { toast.error("Only PDF / JPG / PNG allowed"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File too large (max 15 MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("password", password);
      fd.append("doc_type", docType);
      fd.append("file", file);
      await api.post(`/public/upload-link/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setUploadedCount((n) => n + 1);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex justify-center mb-6"><Logo size={52} withWordmark /></div>

        {!session && !done && (
          <form onSubmit={verify} className="bg-white border border-[#E4E4E7] rounded-2xl p-6 space-y-4" data-testid="ul-verify-form">
            <div className="flex items-center gap-2 text-[#06152D]"><Lock size={18} /><h1 className="font-display text-xl font-bold">Secure document upload</h1></div>
            <p className="text-sm text-[#52525B]">This link was generated by your Loan Junction advisor. Enter the password shared with you separately.</p>
            <div><Label>Password</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="ul-verify-password" /></div>
            <button type="submit" disabled={busy} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-3 rounded-full disabled:opacity-60" data-testid="ul-verify-submit">
              {busy ? "Verifying…" : "Continue"}
            </button>
          </form>
        )}

        {session && !done && (
          <div className="bg-white border border-[#E4E4E7] rounded-2xl p-6 space-y-4" data-testid="ul-upload-panel">
            <div>
              <div className="flex items-center gap-2 text-[#06152D]"><Upload size={18} /><h1 className="font-display text-xl font-bold">Hi {session.customer_name.split(" ")[0]}</h1></div>
              <p className="text-sm text-[#52525B]">Please upload each document below. PDF / JPG / PNG only · max 15 MB per file.</p>
              <p className="mt-1 text-[11px] text-[#EA580C] inline-flex items-center gap-1"><Clock size={11} /> Link expires {new Date(session.expires_at).toLocaleString("en-IN")}. Single-use.</p>
            </div>
            <div className="space-y-3">
              {(session.doc_types || []).map((d) => (
                <div key={d.key} className="border border-[#E4E4E7] rounded-lg p-3 flex items-center gap-3" data-testid={`ul-doc-${d.key}`}>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#06152D]">{d.label}</div>
                    {d.required && <div className="text-[10px] text-[#DC2626]">Required</div>}
                  </div>
                  <label className="bg-[#0052FF] hover:bg-[#003FCC] text-white text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer">
                    Upload
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/jpg,image/png"
                      className="hidden"
                      onChange={(e) => upload(d.key, e.target.files?.[0])}
                      data-testid={`ul-file-${d.key}`}
                    />
                  </label>
                </div>
              ))}
              {(!session.doc_types || session.doc_types.length === 0) && (
                <div className="text-sm text-[#52525B]">No specific document list configured. You may upload any file below.</div>
              )}
            </div>
          </div>
        )}

        {done && (
          <div className="bg-white border border-[#E4E4E7] rounded-2xl p-8 text-center" data-testid="ul-done">
            <CheckCircle2 size={56} className="mx-auto text-[#059669]" />
            <h1 className="mt-4 font-display text-2xl font-bold text-[#06152D]">Thanks!</h1>
            <p className="mt-2 text-sm text-[#52525B]">Your document{uploadedCount > 1 ? "s have" : " has"} been received securely. Your Loan Junction advisor will get in touch.</p>
            <p className="mt-4 text-[10px] text-[#52525B]">This link is now closed. If more documents are needed, request a fresh link.</p>
          </div>
        )}

        <div className="mt-8 text-center text-[10px] text-[#52525B]">
          Secured with encrypted upload · single-use token · audit-logged<br/>
          © {new Date().getFullYear()} Loan Junction Finance Services
        </div>
      </div>
    </div>
  );
}
